import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldAlert,
  Scale,
  Cloud,
  Activity,
  Compass,
  ClipboardList,
  Database,
  Network,
  AlertTriangle,
  Leaf,
} from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";

const reasons = [
  {
    icon: ShieldAlert,
    title: "Datalekken worden bestuurlijke crises",
    text:
      "Wanneer persoonsgegevens op straat belanden, gaat de discussie niet alleen over beveiliging. Bestuurders moeten kunnen uitleggen welke data aanwezig was, wie toegang had, welke maatregelen waren genomen en hoe inwoners, huurders, cliënten of medewerkers worden geïnformeerd.",
  },
  {
    icon: Scale,
    title: "Algoritmen vragen om verantwoording",
    text:
      "Organisaties gebruiken steeds vaker algoritmische systemen bij processen zoals vergunningverlening, Woo verzoeken, fraudepreventie, risicoselectie, toewijzing of prioritering. Zonder overzicht, register en uitlegbaarheid ontstaat bestuurlijk risico.",
  },
  {
    icon: Cloud,
    title: "Cloudafhankelijkheid vraagt om regie",
    text:
      "Veel organisaties zijn afhankelijk van grote cloud en SaaS leveranciers. Dat hoeft niet verkeerd te zijn, maar het vraagt wel om bewustzijn van juridische context, leveranciersmacht, datastromen, exitmogelijkheden en continuïteit.",
  },
  {
    icon: Activity,
    title: "AI gebruik groeit sneller dan beleid",
    text:
      "Medewerkers gebruiken AI functies in tekstverwerking, e-mail, zoekmachines, zaaksystemen en externe tools. Vaak ontstaan nieuwe werkwijzen sneller dan beleid, toezicht en verantwoordingsstructuren kunnen volgen.",
  },
];

const expertise = [
  {
    icon: Compass,
    title: "AI governance",
    text:
      "Wij helpen organisaties in kaart brengen welke AI toepassingen en algoritmische systemen worden gebruikt, welke risico's daarbij horen en welke governance nodig is rond beleid, toezicht, uitlegbaarheid en menselijke controle.",
  },
  {
    icon: ClipboardList,
    title: "Algoritmeregisters",
    text:
      "SovEU helpt organisaties toe te werken naar een praktisch algoritmeregister. Niet als papieren verplichting, maar als instrument om besluiten, systemen, leveranciers, risico's en verantwoordelijkheden inzichtelijk te maken.",
  },
  {
    icon: Database,
    title: "Data governance",
    text:
      "Wij brengen in beeld welke gevoelige gegevens worden verwerkt, waar deze gegevens staan, wie toegang heeft en welke datastromen bestuurlijke aandacht vragen.",
  },
  {
    icon: Network,
    title: "Cloud en leveranciersregie",
    text:
      "Wij analyseren afhankelijkheden van cloudplatforms, SaaS leveranciers en kritieke digitale diensten. Daarbij kijken we naar jurisdictie, leveranciersmacht, lock in, continuïteit en bestuurlijke beheersbaarheid.",
  },
  {
    icon: AlertTriangle,
    title: "Bestuurlijke incidentvoorbereiding",
    text:
      "SovEU helpt organisaties nadenken over wat er bestuurlijk moet gebeuren als data verloren gaat, een algoritme ter discussie staat of een toezichthouder vragen stelt. Wie communiceert wat, aan wie, wanneer en op basis van welke informatie?",
  },
  {
    icon: Leaf,
    title: "Digitale voetafdruk van AI",
    text:
      "Waar relevant brengen wij ook de proportionaliteit en indicatieve energie impact van AI gebruik in beeld. Niet als formele CO2 audit, maar als startpunt voor duurzaamheidsbeleid, interne afwegingen en leveranciersgesprekken.",
  },
];

const steps = [
  {
    n: "01",
    title: "Eerst overzicht",
    text:
      "We brengen in kaart welke systemen, AI toepassingen, algoritmen, cloudservices, leveranciers en datastromen relevant zijn voor de organisatie.",
  },
  {
    n: "02",
    title: "Dan beoordeling",
    text:
      "We beoordelen elk relevant systeem aan de hand van de SovEU Regiemethode. Daarbij kijken we naar gebruik, data, toegang, jurisdictie, uitlegbaarheid, toezicht, continuïteit en waar relevant de digitale voetafdruk.",
  },
  {
    n: "03",
    title: "Daarna bestuurlijke prioriteiten",
    text:
      "Niet elk risico is even urgent. Wij vertalen de analyse naar bestuurlijke prioriteiten, zodat directie, bestuur of management weet wat eerst aandacht vraagt.",
  },
  {
    n: "04",
    title: "Tot slot concrete actie",
    text:
      "Elke opdracht eindigt met concrete vervolgstappen. Denk aan een actieplan, beleid, registers, een governance kader, incidentvoorbereiding of periodieke regieborging.",
  },
];

const audiences = [
  "Gemeenten",
  "Omgevingsdiensten",
  "Veiligheidsregio's",
  "GGD'en",
  "Waterschappen",
  "Woningcorporaties",
  "Onderwijsinstellingen",
  "Zorgorganisaties",
  "Semipublieke organisaties",
  "Maatschappelijke organisaties",
  "Gereguleerde organisaties op aanvraag",
];

export default function WieZijnWij() {
  return (
    <div data-testid="page-wie-zijn-wij" className="pt-[96px]">
      <SeoHead
        title="Over SovEU"
        description="SovEU is opgericht om publieke en semipublieke organisaties te helpen grip te krijgen op AI, algoritmen, cloudafhankelijkheid, data en digitale regie."
        path="/over-soveu"
      />

      {/* ===== 1. Hero ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="wzw-hero"
      >
        <div className="container-soveu max-w-4xl text-white">
          <FadeIn>
            <div
              className="text-[13px] font-medium tracking-[0.14em] uppercase mb-6"
              style={{ color: "#9DB6D1" }}
            >
              Wie zijn wij
            </div>
            <h1 className="h1-display text-white mb-7">
              SovEU helpt publieke organisaties digitale regie terug te nemen.
            </h1>
            <p className="body-lg text-white/75 max-w-3xl mb-9">
              Een digitaal onafhankelijk Europa begint niet met grote woorden, maar met concreet inzicht in systemen, data, algoritmen, leveranciers en verantwoordelijkheden. SovEU ondersteunt organisaties die van afhankelijkheid naar regie willen bewegen — praktisch, onafhankelijk en bestuurlijk bruikbaar.
            </p>
            <Link to="/contact" data-testid="wzw-hero-cta" className="btn-primary btn-on-navy">
              Plan een kennismakingsgesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ===== 2. Mission ===== */}
      <section className="section bg-white" data-testid="wzw-mission">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <div className="eyebrow mb-3">Onze missie</div>
            <h2 className="h2-display mb-7">Onze missie</h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              SovEU is opgericht vanuit een eenvoudige overtuiging: publieke organisaties moeten kunnen uitleggen hoe zij omgaan met data, algoritmen, AI toepassingen en digitale leveranciers. Niet pas nadat er een datalek, politieke discussie of toezichtszaak ontstaat, maar vooraf.
            </p>
            <p>
              Gemeenten, woningcorporaties, onderwijsinstellingen, zorgorganisaties en andere semipublieke instellingen werken dagelijks met systemen die steeds complexer worden. Cloudplatforms verwerken gevoelige gegevens. Algoritmen ondersteunen besluiten over burgers, huurders, cliënten of studenten. AI functies verschijnen in bestaande software zonder dat bestuurders altijd precies weten wat er gebeurt.
            </p>
            <p>
              SovEU helpt die complexiteit terug te brengen tot bestuurlijke helderheid. Welke systemen zijn er? Welke data gaat waarheen? Welke algoritmen worden gebruikt? Welke leveranciers zijn kritisch? Wie is verantwoordelijk? En welke keuzes moet de organisatie nu maken om aantoonbaar in control te zijn?
            </p>
            <p>
              Onze missie is om publieke en semipublieke organisaties te helpen bewegen van compliance naar controle, van afhankelijkheid naar regie en van reactief handelen naar bestuurlijk voorbereid zijn.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== 3. Why SovEU exists ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="wzw-why"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Waarom SovEU bestaat</h2>
            <p className="body-lg text-[#334155]">
              Digitale risico's zijn geen technisch zijpad meer. Ze raken bestuur, toezicht, vertrouwen en publieke legitimiteit.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {reasons.map((r, i) => (
              <FadeIn key={r.title} delay={i * 80}>
                <article
                  className="soveu-card p-7 h-full"
                  data-testid={`wzw-reason-${i + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-md inline-flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                    >
                      <r.icon size={22} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                      <h3 className="h3-display mb-3">{r.title}</h3>
                      <p className="text-[#334155] leading-relaxed">{r.text}</p>
                    </div>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. Expertise ===== */}
      <section className="section bg-white" data-testid="wzw-expertise">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Onze expertise</div>
            <h2 className="h2-display mb-5">Onze expertise</h2>
            <p className="body-lg text-[#334155]">
              SovEU verbindt bestuurlijke ervaring, kennis van cybersecurity, inzicht in publieke besluitvorming en Europese digitale regelgeving. De kracht zit niet in technische diepte alleen, maar in het vertalen van digitale risico's naar bestuurlijke keuzes.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertise.map((e, i) => (
              <FadeIn key={e.title} delay={i * 60}>
                <article
                  className="soveu-card p-7 h-full"
                  data-testid={`wzw-expertise-${i + 1}`}
                >
                  <div
                    className="w-11 h-11 rounded-md inline-flex items-center justify-center mb-5"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    <e.icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className="h3-display mb-3">{e.title}</h3>
                  <p className="text-[#334155] leading-relaxed text-[15px]">{e.text}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. How we work ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="wzw-how"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Hoe wij werken</h2>
            <p className="body-lg text-[#334155]">
              SovEU werkt met een vaste methode, duidelijke producten en bestuurlijk bruikbare output. Geen open einde trajecten, geen onnodig jargon en geen rapporten die in een la verdwijnen.
            </p>
          </FadeIn>

          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 80}>
                <li
                  className="soveu-card p-7 h-full"
                  data-testid={`wzw-step-${s.n}`}
                >
                  <div
                    className="text-[13px] font-semibold mb-3 tracking-[0.14em]"
                    style={{ color: "#2D6F8F" }}
                  >
                    Stap {s.n}
                  </div>
                  <h3 className="text-[18px] font-semibold mb-3 leading-snug" style={{ color: "#1E3A5F" }}>
                    {s.title}
                  </h3>
                  <p className="text-[#334155] leading-relaxed text-[15px]">{s.text}</p>
                </li>
              </FadeIn>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== 6. Independence + EU ===== */}
      <section className="section bg-white" data-testid="wzw-eu">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <div className="eyebrow mb-3">Europese verankering</div>
            <h2 className="h2-display mb-7">
              Onafhankelijk, Europees en bestuurlijk verantwoord
            </h2>
          </FadeIn>
          <FadeIn delay={100} className="space-y-5 body-lg text-[#334155] leading-relaxed">
            <p>
              SovEU werkt vanuit het uitgangspunt dat publieke organisaties hun digitale keuzes moeten kunnen uitleggen. Dat vraagt om meer dan voldoen aan regels. Het vraagt om aantoonbare regie.
            </p>
            <p>
              Wij sluiten aan bij de richting van Europese regelgeving en normen rond gegevensbescherming, AI, informatiebeveiliging, digitale weerbaarheid en publieke verantwoording. Denk aan de AVG, de EU AI Act, BIO, NIS2, DORA waar relevant en de bredere ontwikkeling naar transparant en verantwoord algoritmegebruik.
            </p>
            <p>
              SovEU geeft geen formele compliance certificering en vervangt geen juridisch advies. Wij helpen organisaties wel om de juiste vragen te stellen, de juiste informatie boven tafel te krijgen en bestuurlijk beter voorbereid te zijn.
            </p>
            <p>
              Een digitaal onafhankelijk Europa is geen abstract ideaal. Het is een keuze die begint bij weten welke systemen je gebruikt, welke data je verwerkt, wie toegang heeft en welke afhankelijkheden je accepteert.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== 7. Audiences ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="wzw-audiences"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-5">Voor wie wij werken</h2>
            <p className="body-lg text-[#334155]">
              SovEU werkt voor organisaties die publieke of maatschappelijke verantwoordelijkheid dragen en daarom zorgvuldig moeten omgaan met data, technologie en vertrouwen.
            </p>
          </FadeIn>

          <div className="flex flex-wrap gap-3">
            {audiences.map((a, i) => (
              <FadeIn key={a} delay={i * 30}>
                <span
                  className="soveu-card inline-flex px-5 py-3 text-[15px] font-medium"
                  style={{ color: "#1E3A5F" }}
                  data-testid={`wzw-audience-${i + 1}`}
                >
                  {a}
                </span>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={400}>
            <p className="mt-10 max-w-3xl text-[#334155] leading-relaxed body-lg">
              De gemene deler is niet de sector, maar de verantwoordelijkheid. Organisaties die werken met gevoelige data, publieke taken, maatschappelijke belangen of bestuurlijke verantwoording moeten kunnen aantonen dat zij grip hebben op hun digitale omgeving.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== 8. CTA ===== */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="wzw-cta"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">
              Wilt u weten waar uw organisatie staat?
            </h2>
            <p className="body-lg text-white/75 mb-9">
              Een eerste gesprek maakt snel duidelijk of een Bestuursscan, Regiebeeld of Regiekader passend is. SovEU denkt graag mee over de vraag hoe uw organisatie grip krijgt op AI gebruik, cloudafhankelijkheden, algoritmen en datarisico's.
            </p>
            <Link to="/contact" data-testid="wzw-final-cta" className="btn-primary btn-on-navy">
              Plan een gesprek
              <ArrowRight size={18} />
            </Link>
            <p className="mt-6 text-[14px] text-white/70">
              Of mail direct naar{" "}
              <a
                href="mailto:info@soveu.nl?subject=Kennismaking%20SovEU"
                className="underline underline-offset-4 hover:text-white"
              >
                info@soveu.nl
              </a>
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
